/**
 * Copyright (C) 2019 Florian Rommel, Kwok-Fung Choi
 *
 * The JavaScript code in this file is free software: you can
 * redistribute it and/or modify it under the terms of the GNU
 * General Public License (GNU GPL) as published by the Free Software
 * Foundation, either version 3 of the License, or (at your option)
 * any later version.  The code is distributed WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE.  See the GNU GPL for more details.
 *
 * As additional permission under GNU GPL version 3 section 7, you
 * may distribute non-source (e.g., minimized or compacted) forms of
 * that code without the copy of the GNU GPL normally required by
 * section 4, provided you include this license notice and a URL
 * through which recipients can access the Corresponding Source.
 *
 */

import Code from "./code.js";
import Wire from "./wires/wire.js";
import ActiveOverwrite from "./info/active_overwrite.js";
import InstructionWire from "./wires/instruction_wire.js";
import Rew from "./rew.js";
import Pc from "./units/register/pc.js";
import PcAdder from "./units/adder/pcadder.js";
import Mux from "./units/multiplexer/mux.js";
import InstructionMem from "./units/memory/instruction_mem.js";
import Ifid from "./units/register/if_id.js";
import MainControl from "./units/control/main_control.js";
import MuxRegdest from "./units/multiplexer/mux_reg_dest.js";
import Registers from "./units/memory/registers.js";
import SignExtend from "./units/sign_extend.js";
import Extend from "./units/extend.js";
import Idex from "./units/register/id_ex.js";
import ShiftLeft2 from "./units/shiftleft2.js";
import BrjmpAdder from "./units/adder/brjmpadder.js";
import ForwardingUnit from "./units/control/forwarding.js";
import MuxFw from "./units/multiplexer/mux_fw.js";
import Alu from "./units/alu.js";
import Exmem from "./units/register/ex_mem.js";
import DataMem from "./units/memory/data_mem.js";
import AndBrzero from "./units/gates/and_br_zero.js";
import OrBrjump from "./units/gates/or_br_jump.js";
import Memwb from "./units/register/mem_wb.js";
import ConflictDetection from "./units/control/conflict_detection.js";
import { SimInfo } from "./info/sim_info.js";
import PlBorder from "./units/register/pl_border.js";
import PlReg from "./units/register/pl_reg.js";
import HazardDetection from "./units/control/hazard_detection.js";
import { Types, SpecialTypes } from "./types.js";


/**
 * Class representing the main simulator.
 * Acts as the main model in the MVC like project structure.
 */
export default class Simulator {
    /**
     * Creates the Simulation and connects all units and wires.
     * By default the forwarding unit and the hazard detection unit are turned on
     * and the conflict detection unit is turned off.
     */
    constructor() {
        this.clock = 0;
        this.code = new Code();
        this.hazard_mode = 'forwarding';
        this.finished = false;
        this.pipelining_mode = true;
        this.support_for_shift = false;

        // setup all the wires and active identfiers
        this.wires = {
            p_if: {
                pc: new Wire(32),
                pc_before_inc: new ActiveOverwrite(),
                pc_before_mux: new ActiveOverwrite(),
                inc_pc: new Wire(32),
                new_pc: new Wire(32),
                new_pc_propagate: new ActiveOverwrite(),
                new_pc_feedback: new ActiveOverwrite(),
                final_pc: new Wire(32),
                instruction: new InstructionWire(),
                trashed: new Wire(2),
                four: new Wire(32, 4),
            },
            p_id: {
                instruction: new InstructionWire(),
                new_pc: new Wire(32),
                opcode: new Wire(6),
                funct: new Wire(6),
                immediate: new Wire(26),
                read_register1: new Wire(5),
                read_register2: new Wire(5),
                shamt: new Wire(5),
                shamt_extended: new Wire(32),
                op_write_register: new Wire(10),

                aluop: new Wire(4),
                branch: new Wire(1),
                jump: new Wire(1),
                shamt_ctrl: new Wire(1),
                memread: new Wire(1),
                memwrite: new Wire(1),
                regwrite: new Wire(1),
                regdest: new Wire(1),
                alusrc: new Wire(1),

                write_register: new Wire(5),
                read_data1: new Wire(32),
                read_d1_or_shamt: new Wire(32),
                read_data2: new Wire(32),
                extended_immediate: new Wire(32),
            },

            p_ex: {
                instruction: new InstructionWire(),
                new_pc: new Wire(32),
                brjmp_pc: new Wire(32),
                read_register1: new Wire(5),
                read_register2: new Wire(5),

                aluop: new Wire(4),
                branch: new Wire(1),
                jump: new Wire(1),
                memread: new Wire(1),
                memwrite: new Wire(1),
                regwrite: new Wire(1),
                regdest: new Wire(1),
                alusrc: new Wire(1),

                write_register: new Wire(5),
                read_data1: new Wire(32),
                read_d1_or_shamt: new Wire(32),
                read_data2: new Wire(32),
                read_data1_conflict: new Rew(false),
                read_data2_conflict: new Rew(false),
                extended_immediate: new Wire(32),
                extended_immediate_before_shift: new ActiveOverwrite(),
                extended_immediate_before_alu: new ActiveOverwrite(),
                shifted_immediate: new Wire(32),
                operand2: new Wire(32),
                operand2_alu: new ActiveOverwrite(),
                operand2_mem: new ActiveOverwrite(),
                alu_input1: new Wire(32),
                alu_input2: new Wire(32),
                alu_result: new Wire(32),
                alu_zero: new Wire(1),

                sela: new Wire(2),
                selb: new Wire(2),
            },

            p_mem: {
                instruction: new InstructionWire(),
                brjmp_pc: new Wire(32),

                branch: new Wire(1),
                jump: new Wire(1),
                memread: new Wire(1),
                memwrite: new Wire(1),
                regwrite: new Wire(1),

                write_register: new Wire(5),
                operand2: new Wire(32),
                read_data2: new Wire(32),
                alu_result: new Wire(32),
                alu_result_mem: new ActiveOverwrite(),
                alu_result_propagate: new ActiveOverwrite(),
                alu_result_fwa: new ActiveOverwrite(),
                alu_result_fwb: new ActiveOverwrite(),
                alu_zero: new Wire(1),
                read_data_mem: new Wire(32),

                brzero: new Wire(1),
                branch_or_jump: new Wire(1),
            },

            p_wb: {
                instruction: new InstructionWire(),
                write_register: new Wire(5),
                memread: new Wire(1),
                regwrite: new Wire(1),
                alu_result: new Wire(32),
                read_data_mem: new Wire(32),

                write_data: new Wire(32),
                write_data_fwa: new ActiveOverwrite(),
                write_data_fwb: new ActiveOverwrite(),
            },

            p_cd: {
                stall: new Wire(1),
            }
        };

        // setup all the units
        this.units = {
            pc: new Pc(this.wires.p_if.final_pc, this.wires.p_if.pc,
                this.wires.p_if.trashed, this.wires.p_cd.stall,
                this.wires.p_mem.branch_or_jump, this.wires.p_ex.branch,
                this.wires.p_ex.jump),
            pc_adder: new PcAdder(this.wires.p_if.pc, this.wires.p_if.four,
                this.wires.p_if.inc_pc, this.wires.p_if.pc_before_inc),
            pc_mux_stall: new Mux(this.wires.p_cd.stall,
                [this.wires.p_if.inc_pc,
                this.wires.p_if.pc],
                this.wires.p_if.new_pc),
            instruction_mem: new InstructionMem(this.code,
                this.wires.p_if.pc, this.wires.p_if.instruction),
            ifid: new Ifid(this.wires.p_if, this.wires.p_id, this.wires.p_cd.stall),

            main_control: new MainControl(this.wires.p_id.instruction,
                this.wires.p_id.opcode, this.wires.p_id.funct,
                this.wires.p_id.aluop, this.wires.p_id.branch,
                this.wires.p_id.jump, this.wires.p_id.memread,
                this.wires.p_id.memwrite, this.wires.p_id.regwrite,
                this.wires.p_id.regdest, this.wires.p_id.alusrc,
                this.wires.p_id.shamt_ctrl),
            mux_regdest: new MuxRegdest(this.wires.p_id.regdest,
                this.wires.p_id.op_write_register, this.wires.p_id.write_register),
            registers: new Registers(
                this.wires.p_id.read_register1, this.wires.p_id.read_register2,
                this.wires.p_id.read_data1, this.wires.p_id.read_data2,
                this.wires.p_wb.regwrite, this.wires.p_wb.write_register,
                this.wires.p_wb.write_data, this.wires.p_id.instruction,
                this.wires.p_wb.instruction, this.wires.p_id.shamt_ctrl),
            sign_extend: new SignExtend(this.wires.p_id.jump,
                this.wires.p_id.immediate, this.wires.p_id.extended_immediate),
            sign_extend_sa: new Extend(this.wires.p_id.shamt, this.wires.p_id.shamt_extended),
            mux_shamt: new Mux(this.wires.p_id.shamt_ctrl,
                [this.wires.p_id.read_data1,
                this.wires.p_id.shamt_extended],
                this.wires.p_id.read_d1_or_shamt),
            idex: new Idex(this.wires.p_id, this.wires.p_ex, this.wires.p_cd.stall),

            shift_left: new ShiftLeft2(this.wires.p_ex.extended_immediate,
                this.wires.p_ex.shifted_immediate,
                this.wires.p_ex.extended_immediate_before_shift),
            brjmp_adder: new BrjmpAdder(this.wires.p_ex.new_pc,
                this.wires.p_ex.shifted_immediate, this.wires.p_ex.brjmp_pc,
                this.wires.p_ex.instruction),
            forwarding_unit: new ForwardingUnit(
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2,
                this.wires.p_mem.write_register, this.wires.p_wb.write_register,
                this.wires.p_mem.regwrite, this.wires.p_wb.regwrite,
                this.wires.p_ex.alusrc, this.wires.p_ex.sela,
                this.wires.p_ex.selb, this.wires.p_ex.instruction),
            fw_mux_sela: new MuxFw(this.wires.p_ex.sela,
                this.wires.p_ex.read_data1, this.wires.p_ex.alu_input1,
                this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2),
            fw_mux_selb: new MuxFw(this.wires.p_ex.selb,
                this.wires.p_ex.read_data2, this.wires.p_ex.operand2,
                this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2),
            mux_alusrc: new Mux(this.wires.p_ex.alusrc,
                [this.wires.p_ex.operand2,
                this.wires.p_ex.extended_immediate],
                this.wires.p_ex.alu_input2),
            alu: new Alu(this.wires.p_ex.aluop,
                this.wires.p_ex.alu_input1, this.wires.p_ex.alu_input2,
                this.wires.p_ex.alu_result, this.wires.p_ex.alu_zero,
                this.wires.p_ex.instruction, this.wires.p_ex.branch),
            exmem: new Exmem(this.wires.p_ex, this.wires.p_mem),

            data_mem: new DataMem(this.wires.p_mem.memread,
                this.wires.p_mem.memwrite, this.wires.p_mem.alu_result,
                this.wires.p_mem.operand2, this.wires.p_mem.read_data_mem,
                this.wires.p_mem.instruction),
            and_brzero: new AndBrzero(this.wires.p_mem.branch,
                this.wires.p_mem.alu_zero, this.wires.p_mem.brzero),
            or_brjump: new OrBrjump(this.wires.p_mem.brzero,
                this.wires.p_mem.jump, this.wires.p_mem.branch_or_jump),
            pc_mux_brjmp: new Mux(this.wires.p_mem.branch_or_jump,
                [this.wires.p_if.new_pc,
                this.wires.p_mem.brjmp_pc],
                this.wires.p_if.final_pc),
            memwb: new Memwb(this.wires.p_mem, this.wires.p_wb,
                this.wires.p_mem.alu_result_propagate),
            mux_memread: new Mux(this.wires.p_wb.memread,
                [this.wires.p_wb.alu_result,
                this.wires.p_wb.read_data_mem],
                this.wires.p_wb.write_data),
            hazard_detection: new HazardDetection(
                this.wires.p_id.read_register1, this.wires.p_id.read_register2,
                this.wires.p_id.jump, this.wires.p_id.alusrc,
                this.wires.p_ex.write_register, this.wires.p_ex.jump,
                this.wires.p_ex.branch, this.wires.p_ex.memread,
                this.wires.p_mem.jump, this.wires.p_mem.brzero,
                this.wires.p_cd.stall, this.wires.p_ex.instruction,
                this.wires.p_mem.instruction),
            conflict_detection: new ConflictDetection(
                this.wires.p_ex.regwrite, this.wires.p_mem.regwrite,
                this.wires.p_wb.regwrite, this.wires.p_ex.regdest,
                this.wires.p_ex.write_register, this.wires.p_id.read_register1,
                this.wires.p_id.read_register2, this.wires.p_mem.write_register,
                this.wires.p_wb.write_register, this.wires.p_ex.jump,
                this.wires.p_ex.branch, this.wires.p_mem.jump,
                this.wires.p_mem.brzero, this.wires.p_cd.stall,
                this.wires.p_ex.instruction, this.wires.p_mem.instruction,
                this.wires.p_wb.instruction, this.wires.p_id.jump),
        };
        // trashed objects not connected to the simulator.
        // needed so switching hazard mode can rewind properly
        this.trash = {
            trash_wire1: new Wire(1),
            trash_wire32: new Wire(32),
        }

        this.reset_info();
    }

    /**
     * Reloads the info from the SimInfo class.
     */
    reset_info() {
        if (SimInfo) {
            Object.values(this.wires).forEach((stage) => {
                Object.entries(stage)
                    .filter((entry) => !(entry[1] instanceof ActiveOverwrite))
                    .forEach((entry) => entry[1].info = SimInfo.wires[entry[0]]);
            });
            Object.entries(this.units).forEach((entry) => {
                entry[1].info = SimInfo.units[entry[0]];
                if (entry[1] instanceof PlBorder) {
                    Object.entries(entry[1])
                        .filter((inner) => inner[1] instanceof PlReg)
                        .forEach((inner) => inner[1].info = SimInfo.wires[inner[0]]);
                }
            });
        }
    }

    /**
     * Returns the current clock.
     * @returns {num} - The clock.
     */
    get_clock() {
        return this.clock;
    }

    /**
     * Gets the instructions at the requested phase as a three-part string:
     * First index describes the phase: [0]: IF ... [4]: WB,
     * Second index describes the instruction:
     *   [0]: The instruction
     *   [1]: The operands
     *   [2]: The type of operands e.g. source-register, immediate etc.
     *   [3]: Extra info
     * @param {number} - The target clock.
     * @returns {Array<Array<String>>} - The instruction as a two-dimensional string array
     */
    get_executing_instructions(clock) {
        let instructions = [];
        for (let phase in this.wires) {
            if (this.wires[phase].instruction) {
                let index = this.wires[phase].instruction.get_index(clock)
                let instr_array = this.wires[phase].instruction
                    .get(clock).get_as_formatted_string_array(index);
                if (this.wires[phase].trashed) {
                    let trashed = this.wires[phase].trashed.value.get(clock);
                    if (trashed == 1) {
                        for (let type in instr_array[2]) {
                            instr_array[2][type] = "trashed";
                        }
                        instr_array[3] = "Branch?";
                    } else if (trashed == 2) {
                        for (let type in instr_array[2]) {
                            instr_array[2][type] = "trashed";
                        }
                        instr_array[3] = "Jump!";
                    }
                }
                instructions.push(instr_array);
            }
        }
        return instructions;
    }

    /**
     * Returns the code in a formatted string array.
     * @param {number} clock - The target clock. 
     */
    get_formatted_code(clock) {
        let code_arr = this.code.get_formatted_code();
        for (let index in code_arr) {
            let pc = "";
            let address = index * 4;
            code_arr[index].unshift("" + address);

            let currently_loaded = this.wires.p_if.pc.value.get(clock)
                == parseInt(index) * 4;
            if (currently_loaded) {
                pc = "PC";
            }
            code_arr[index].unshift(pc);
        }
        return code_arr;
    }

    /**
     * Returns the registers values in a formatted string array.
     * @param {number} clock - The target clock.
     */
    get_formatted_registers(clock) {
        let reg_arr = this.units.registers.get_formatted_registers(clock);
        return reg_arr;
    }

    /**
     * Returns all information on wires, units etc. in the simulation.
     * @param {number} clock - The target clock.
     * @param {boolean} isShort - Should it return the short format?
     */
    get_unit_and_wire_info(clock, isShort) {
        let components = {};
        components['wires'] = {};
        components['units'] = {};
        components['stopped'] = {};
        for (let stage in this.wires) {
            components['wires'][stage] = {};
            for (let wire in this.wires[stage]) {
                components['wires'][stage][wire] = this.wires[stage][wire]
                    .get_info(clock, isShort);
            }
            if (stage == 'p_if') {
                components['stopped'][stage] = this.wires[stage].trashed
                    .value.get(clock) > 0;
            } else if (['p_ex', 'p_mem', 'p_wb'].includes(stage)) {
                components['stopped'][stage] = this.wires[stage].instruction
                    .get(clock).type == SpecialTypes.STALL;
            }
        }
        for (let unit in this.units) {
            components['units'][unit] = this.units[unit]
                .get_info(this.clock, isShort);
        }
        return components;
    }

    /**
     * Returns the current settings.
     */
    get_settings() {
        let settings = {};
        if (this.pipelining_mode) {
            settings['mode'] = 'pipeline';
        } else {
            settings['mode'] = 'sequential';
        }

        settings['hazard'] = this.hazard_mode;

        //for display in svg (adds a unit)
        settings['shift_support'] = this.support_for_shift;

        return settings;
    }

    /**
     * Changes the settings to the ones given.
     */
    change_settings(settings) {
        switch (settings['hazard']) {
            case 'forwarding':
                this.set_to_forwarding()
                break;
            case 'conflict':
                this.set_to_conflict_detection();
                break;
            case 'none':
                this.set_to_no_detection();
                break;
        }
        switch (settings['mode']) {
            case 'pipeline':
                this.turn_on_pipelining();
                break;
            case 'sequential':
                this.turn_off_pipelining();
                break;
        }
    }

    /**
     * Returns the instructions supported by the simulator.
     */
    get_supported_instructions() {
        return Types;
    }

    /**
     * Replaces the old code with the new one and sets support for shift accordingly.
     * @param {Array<Instruction>} code - The new code.
     */
    set_code(code) {
        let has_shift = false;
        for (let instr in code) {
            let is_shift = (code[instr].funct <= 2) && (code[instr].type.format == "R");
            has_shift = has_shift || is_shift;
        }
        if (has_shift) {
            this.add_support_for_shift();
            this.units.main_control.add_shift();
        } else {
            this.remove_support_for_shift();
            this.units.main_control.remove_shift();
        }
        this.code.set_code(code);
    }

    /**
     * Returns the data memory at a certain clock.
     * @param {number} clock - The target clock.
     */
    get_data_mem(clock) {
        return this.units.data_mem.get_data_mem(clock);
    }

    /**
     * Makes the simulator compute one clock.
     */
    step() {
        if (this.finished && this.clock != 0) return; // PL has already processed all instructions

        if (this.pipelining_mode) {// instructions should be executed in pipelined fashion
            this.clock += 1;

            this.do_IF_comb_actions();
            this.do_WB_comb_actions();
            this.do_ID_comb_actions();
            this.do_EX_comb_actions();
            this.do_MEM_comb_actions();
            this.do_FW_actions();
            this.do_CD_actions();
            this.do_clock_actions();

        } else { // sequential execution
            this.clock += 1;
            let phase = (this.clock - 1) % 5;
            this.reset_active_overwrites();

            switch (phase) {
                case 0:
                    this.seq_IF_actions();
                    break;
                case 1:
                    this.seq_ID_actions();
                    break;
                case 2:
                    this.seq_EX_actions();
                    break;
                case 3:
                    this.seq_MEM_actions();
                    break;
                default:
                    this.seq_WB_actions();
                    break;
            }
        }
        this.finished = this.is_finished();
    }

    /**
     * The combinatorial actions in the IF phase.
     * Does not include the adder and the multiplexer (conflict detection)
     */
    do_IF_comb_actions() {
        this.units.pc.do_comb(this.clock);
        this.units.instruction_mem.do_comb(this.clock);
    }

    /**
     * The combinatorial actions in the ID phase.
     */
    do_ID_comb_actions() {
        this.units.ifid.do_comb(this.clock);
        this.units.main_control.do_comb(this.clock);
        this.units.mux_regdest.do_comb(this.clock);
        this.units.registers.do_comb(this.clock);
        this.units.sign_extend.do_comb(this.clock);
        this.units.sign_extend_sa.do_comb(this.clock);
        this.units.mux_shamt.do_comb(this.clock);
    }

    /**
     * The combinatorial actions in the EX phase.
     * Does not include ALU selection and ALU exec (see Forwarding)
     */
    do_EX_comb_actions() {
        this.units.idex.do_comb(this.clock);
        let alusrc = this.wires.p_ex.alusrc.value.get();
        let jump = this.wires.p_ex.jump.value.get();
        let branch = this.wires.p_ex.branch.value.get();
        let memwrite = this.wires.p_ex.memwrite.value.get();
        this.wires.p_ex.extended_immediate_before_shift.active
            .set(this.clock, jump || branch);
        this.wires.p_ex.extended_immediate_before_alu.active
            .set(this.clock, alusrc);
        this.wires.p_ex.operand2_mem.active
            .set(this.clock, memwrite);
        this.units.shift_left.do_comb(this.clock);
        this.units.brjmp_adder.do_comb(this.clock);
    }

    /**
     * The combinatorial actions in the MEM phase.
     */
    do_MEM_comb_actions() {
        this.units.exmem.do_comb(this.clock);
        let memread = this.wires.p_mem.memread.value.get();
        let memwrite = this.wires.p_mem.memwrite.value.get();
        let regwrite = this.wires.p_mem.regwrite.value.get();
        this.wires.p_mem.alu_result_mem.active.set(this.clock, memread || memwrite);
        this.wires.p_mem.alu_result_propagate.active
            .set(this.clock, !(memread || memwrite) && regwrite);
        this.units.data_mem.do_comb(this.clock);
        this.units.and_brzero.do_comb(this.clock);
        this.units.or_brjump.do_comb(this.clock);
    }

    /**
     * The combinatorial actions in the WB phase.
     */
    do_WB_comb_actions() {
        this.units.memwb.do_comb(this.clock);
        this.units.mux_memread.do_comb(this.clock);
    }

    /**
     * The Forwarding actions.
     */
    do_FW_actions() {
        this.units.forwarding_unit.do_comb(this.clock);
        this.units.fw_mux_sela.do_comb(this.clock);
        this.units.fw_mux_selb.do_comb(this.clock);
        let sela = this.wires.p_ex.sela.value.get();
        let selb = this.wires.p_ex.selb.value.get();
        this.wires.p_ex.read_data1_conflict.set(this.clock, sela > 0);
        this.wires.p_ex.read_data2_conflict.set(this.clock, selb > 0);
        this.wires.p_mem.alu_result_fwa.active.set(this.clock, sela == 1);
        this.wires.p_mem.alu_result_fwb.active.set(this.clock, selb == 1);
        this.wires.p_wb.write_data_fwa.active.set(this.clock, sela == 2);
        this.wires.p_wb.write_data_fwb.active.set(this.clock, selb == 2);
        this.units.mux_alusrc.do_comb(this.clock);
        this.units.alu.do_comb(this.clock);
        let operand2active = this.wires.p_ex.operand2.active.get();
        let alusrc = this.wires.p_ex.alusrc.value.get();
        this.wires.p_ex.operand2_alu.active
            .set(this.clock, operand2active && !alusrc);
    }

    /**
     * The hazard/conflict detection actions
     */
    do_CD_actions() {
        // Hazard / Conflict detection
        this.units.hazard_detection.do_comb(this.clock);
        this.units.conflict_detection.do_comb(this.clock);
        let stall = this.wires.p_cd.stall.value.get();
        let branch_or_jump = this.wires.p_mem.branch_or_jump.value.get();
        let properties = this.wires.p_if.instruction.get().type.properties;
        this.wires.p_if.pc_before_inc.active.set(this.clock, !stall && !branch_or_jump);
        this.wires.p_if.four.active.set(this.clock, !stall && !branch_or_jump);
        this.wires.p_if.pc_before_mux.active.set(this.clock, stall && !branch_or_jump);
        this.wires.p_if.new_pc_feedback.active.set(this.clock, !branch_or_jump);
        this.wires.p_if.new_pc_propagate.active
            .set(this.clock, !!properties.jump || !!properties.branch);
        this.units.pc_adder.do_comb(this.clock);
        this.units.pc_mux_stall.do_comb(this.clock);
        this.units.pc_mux_brjmp.do_comb(this.clock);
    }

    /**
     * All actions in the IF phase during sequential execution.
     */
    seq_IF_actions() {
        this.units.pc.do_clock(this.clock);
        this.units.pc.do_comb(this.clock)
        this.units.pc.output.active.set(this.clock, true);
        this.units.instruction_mem.do_comb(this.clock);

        let properties = this.wires.p_if.instruction.get().type.properties;
        this.wires.p_if.four.active.set(this.clock, true);
        this.wires.p_if.pc_before_inc.active.set(this.clock, true);
        this.wires.p_if.new_pc_feedback.active.set(this.clock, true);
        this.wires.p_if.new_pc_propagate.active.set(this.clock, properties.jump || properties.branch);

        this.units.pc_adder.do_comb(this.clock);
        this.units.ifid.do_clock(this.clock);
        this.flush_other_stages('p_if');
    }

    /**
     * All actions in the ID phase during sequential execution.
     */
    seq_ID_actions() {
        this.units.ifid.do_comb(this.clock);
        this.units.main_control.do_comb(this.clock);
        this.units.mux_regdest.do_comb(this.clock);
        this.units.registers.do_comb(this.clock);
        this.units.sign_extend.do_comb(this.clock);
        this.units.sign_extend_sa.do_comb(this.clock);
        this.units.mux_shamt.do_comb(this.clock);
        this.units.idex.do_clock(this.clock);
        this.flush_other_stages('p_id');
    }

    /**
     * All actions in the EX phase during sequential execution.
     */
    seq_EX_actions() {
        this.units.idex.do_comb(this.clock);
        let alusrc = this.wires.p_ex.alusrc.value.get();
        let jump = this.wires.p_ex.jump.value.get();
        let branch = this.wires.p_ex.branch.value.get();
        this.wires.p_ex.extended_immediate_before_shift.active
            .set(this.clock, jump || branch);
        this.wires.p_ex.extended_immediate_before_alu.active
            .set(this.clock, alusrc);
        this.units.shift_left.do_comb(this.clock);
        this.units.brjmp_adder.do_comb(this.clock);

        this.units.mux_alusrc.do_comb(this.clock);
        this.units.alu.do_comb(this.clock);
        this.units.exmem.do_clock(this.clock);
        this.flush_other_stages('p_ex');
    }

    /**
     * All actions in the MEM phase during sequential execution.
     */
    seq_MEM_actions() {
        this.units.exmem.do_comb(this.clock);
        let memread = this.wires.p_mem.memread.value.get();
        let memwrite = this.wires.p_mem.memwrite.value.get();
        let regwrite = this.wires.p_mem.regwrite.value.get();
        this.wires.p_mem.alu_result_mem.active.set(this.clock, memread || memwrite);
        this.wires.p_mem.alu_result_propagate.active
            .set(this.clock, !(memread || memwrite) && regwrite);
        this.units.data_mem.do_comb(this.clock);
        this.units.and_brzero.do_comb(this.clock);
        this.units.or_brjump.do_comb(this.clock);

        let branch_or_jump = this.wires.p_mem.branch_or_jump.value.get();
        this.wires.p_if.new_pc_feedback.active.set(this.clock, !branch_or_jump);
        this.units.pc_mux_brjmp.do_comb(this.clock);
        this.wires.p_if.final_pc.active.set(this.clock, true);

        this.units.data_mem.do_clock(this.clock);
        this.units.memwb.do_clock(this.clock);
        this.flush_other_stages('p_mem');
    }

    /**
     * All actions in the WB phase during sequential execution.
     */
    seq_WB_actions() {
        this.units.memwb.do_comb(this.clock);
        this.units.mux_memread.do_comb(this.clock);
        this.units.registers.do_clock(this.clock);
        this.flush_other_stages('p_wb');
    }

    /**
     * Flushes the instruction wire of other stages so they won't show as executing
     * (only relevant for sequential execution).
     * @param {String} - The stage that should not be flushed.
     */
    flush_other_stages(stage) {
        for (let phase in this.wires) {
            if (this.wires[phase].instruction && phase != stage) {
                let off_display = SpecialTypes.OFF.make().type['display'];
                let stage_display = this.wires[phase].instruction.get().type['display'];
                //flush with 'OFF' if not yet flushed
                if (stage_display != off_display) {
                    this.wires[phase].instruction.instr
                        .set(this.clock, SpecialTypes.OFF.make());
                }
            }
        }
    }

    /**
     * Resets all active flags in the simulation.
     */
    reset_active_overwrites() {
        for (let group in this.wires) {
            for (let wire in this.wires[group]) {
                if (this.wires[group][wire].active) {
                    this.wires[group][wire].active.set(this.clock, false);
                }
            }
        }
    }

    /**
     * All the clock (synchronous) actions.
     */
    do_clock_actions() {
        // IF
        this.units.pc.do_clock(this.clock);
        this.units.ifid.do_clock(this.clock);

        // ID
        this.units.registers.do_clock(this.clock);
        this.units.idex.do_clock(this.clock);

        // EX
        this.units.exmem.do_clock(this.clock);

        // MEM
        this.units.data_mem.do_clock(this.clock);
        this.units.memwb.do_clock(this.clock);
    }

    /**
     * Rewinds every Object in simulator to target clock.
     * Notifies views of change.
     * @param {Number} clock - The target clock.
     */
    rewind(clock) {
        this.finished = false;

        let rew = (group) => Object.values(group)
            .forEach((child) => child.rewind(clock));
        rew(this.wires.p_if);
        rew(this.wires.p_id);
        rew(this.wires.p_ex);
        rew(this.wires.p_mem);
        rew(this.wires.p_wb);
        rew(this.wires.p_cd);
        rew(this.units);
        rew(this.trash);
        this.clock = clock;
    }

    /**
     * Immediately jump to specified clock, if instructions have all been
     * processed before reaching clock, terminate function
     * @param {number} clock - The target clock.
     * @throws Will throw an error if clock is negative.
     */
    jump_to(clock) {
        if (clock < 0) {
            throw "Clock should be >= 0";
        } else if (clock < this.clock) {
            this.rewind(clock);
        } else {
            // go through steps until simulator clock is the specified clock
            while (clock > this.clock) {
                if (!this.finished) {
                    this.step();
                } else {
                    window.alert("Simulator finished at clock " + this.clock + "!")
                    return;
                }
            }
        }
    }

    /**
     * Resets the simulation (Rewind to clock cycle 0).
     */
    reset() {
        this.rewind(0);
    }

    /**
     * Checks whether pipeline has no more instruction to process.
     * @returns {boolean} - An indicator if pipeline has finished.
     */
    is_finished() {
        let finished = true;

        let off_instr = SpecialTypes.OFF.make();
        let off_display = off_instr.type['display'];
        let if_display = this.wires.p_if.instruction.get().type['display'];
        let id_display = this.wires.p_id.instruction.get().type['display'];
        let ex_display = this.wires.p_ex.instruction.get().type['display'];
        let mem_display = this.wires.p_mem.instruction.get().type['display'];
        let wb_display = this.wires.p_wb.instruction.get().type['display'];

        finished = finished && (off_display == if_display);
        finished = finished && (off_display == id_display);
        finished = finished && (off_display == ex_display);
        finished = finished && (off_display == mem_display);
        finished = finished && (off_display == wb_display);

        return finished;
    }

    /**
     * Sets the simulator's mode to forwarding.
     */
    set_to_forwarding() {
        this.units.conflict_detection.turn_off();
        this.units.hazard_detection.turn_on();
        this.units.forwarding_unit.turn_on();

        this.units.pc_adder = new PcAdder(this.wires.p_if.pc, this.wires.p_if.four,
            this.wires.p_if.inc_pc, this.wires.p_if.pc_before_inc);
        this.units.pc_mux_stall = new Mux(this.wires.p_cd.stall,
            [this.wires.p_if.inc_pc,
            this.wires.p_if.pc],
            this.wires.p_if.new_pc);

        if (this.support_for_shift) {
            this.units.fw_mux_sela = new MuxFw(this.wires.p_ex.sela,
                this.wires.p_ex.read_d1_or_shamt, this.wires.p_ex.alu_input1,
                this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
        } else {
            this.units.fw_mux_sela = new MuxFw(this.wires.p_ex.sela,
                this.wires.p_ex.read_data1, this.wires.p_ex.alu_input1,
                this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
        }
        this.units.alu = new Alu(this.wires.p_ex.aluop,
            this.wires.p_ex.alu_input1, this.wires.p_ex.alu_input2,
            this.wires.p_ex.alu_result, this.wires.p_ex.alu_zero,
            this.wires.p_ex.instruction, this.wires.p_ex.branch);

        this.units.fw_mux_selb = new MuxFw(this.wires.p_ex.selb,
            this.wires.p_ex.read_data2, this.wires.p_ex.operand2,
            this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
            this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
        this.units.mux_alusrc = new Mux(this.wires.p_ex.alusrc,
            [this.wires.p_ex.operand2,
            this.wires.p_ex.extended_immediate],
            this.wires.p_ex.alu_input2);

        this.units.data_mem = new DataMem(this.wires.p_mem.memread,
            this.wires.p_mem.memwrite, this.wires.p_mem.alu_result,
            this.wires.p_mem.operand2, this.wires.p_mem.read_data_mem,
            this.wires.p_mem.instruction);

        this.hazard_mode = 'forwarding';
        this.reset_info();
    }

    /**
     * Sets the simulator's mode to 'conflict detection'.
     */
    set_to_conflict_detection() {
        this.units.forwarding_unit.turn_off();
        this.units.hazard_detection.turn_off();
        this.units.conflict_detection.turn_on();

        this.units.pc_adder = new PcAdder(this.wires.p_if.pc, this.wires.p_if.four,
            this.wires.p_if.inc_pc, this.wires.p_if.pc_before_inc);
        this.units.pc_mux_stall = new Mux(this.wires.p_cd.stall,
            [this.wires.p_if.inc_pc,
            this.wires.p_if.pc],
            this.wires.p_if.new_pc);

        if (this.support_for_shift) {
            this.units.fw_mux_sela = new MuxFw(this.wires.p_ex.sela,
                this.wires.p_ex.read_d1_or_shamt, this.trash.trash_wire32,
                this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
            this.units.alu = new Alu(this.wires.p_ex.aluop,
                this.wires.p_ex.read_d1_or_shamt, this.wires.p_ex.alu_input2,
                this.wires.p_ex.alu_result, this.wires.p_ex.alu_zero,
                this.wires.p_ex.instruction, this.wires.p_ex.branch);
        } else {
            this.units.fw_mux_sela = new MuxFw(this.wires.p_ex.sela,
                this.wires.p_ex.read_data1, this.trash.trash_wire32,
                this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
            this.units.alu = new Alu(this.wires.p_ex.aluop,
                this.wires.p_ex.read_data1, this.wires.p_ex.alu_input2,
                this.wires.p_ex.alu_result, this.wires.p_ex.alu_zero,
                this.wires.p_ex.instruction, this.wires.p_ex.branch);
        }

        this.units.fw_mux_selb = new MuxFw(this.wires.p_ex.selb,
            this.wires.p_ex.read_data2, this.trash.trash_wire32,
            this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
            this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
        this.units.mux_alusrc = new Mux(this.wires.p_ex.alusrc,
            [this.wires.p_ex.read_data2,
            this.wires.p_ex.extended_immediate],
            this.wires.p_ex.alu_input2);

        this.units.data_mem = new DataMem(this.wires.p_mem.memread,
            this.wires.p_mem.memwrite, this.wires.p_mem.alu_result,
            this.wires.p_mem.read_data2, this.wires.p_mem.read_data_mem,
            this.wires.p_mem.instruction);

        this.hazard_mode = 'conflict';
        this.reset_info();
    }

    /**
     * Changes the simulator's mode to 'no hazard detection'.
     */
    set_to_no_detection() {
        this.units.forwarding_unit.turn_off();
        this.units.hazard_detection.turn_off();
        this.units.conflict_detection.turn_off();

        this.units.pc_adder = new PcAdder(this.wires.p_if.pc, this.wires.p_if.four,
            this.wires.p_if.new_pc, this.wires.p_if.pc_before_inc);
        this.units.pc_mux_stall = new Mux(this.trash.trash_wire1,
            [this.trash.trash_wire32], this.trash.trash_wire32);

        if (this.support_for_shift) {
            this.units.fw_mux_sela = new MuxFw(this.wires.p_ex.sela,
                this.wires.p_ex.read_d1_or_shamt, this.trash.trash_wire32,
                this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
            this.units.alu = new Alu(this.wires.p_ex.aluop,
                this.wires.p_ex.read_d1_or_shamt, this.wires.p_ex.alu_input2,
                this.wires.p_ex.alu_result, this.wires.p_ex.alu_zero,
                this.wires.p_ex.instruction, this.wires.p_ex.branch);
        } else {
            this.units.fw_mux_sela = new MuxFw(this.wires.p_ex.sela,
                this.wires.p_ex.read_data1, this.trash.trash_wire32,
                this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
                this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
            this.units.alu = new Alu(this.wires.p_ex.aluop,
                this.wires.p_ex.read_data1, this.wires.p_ex.alu_input2,
                this.wires.p_ex.alu_result, this.wires.p_ex.alu_zero,
                this.wires.p_ex.instruction, this.wires.p_ex.branch);
        }

        this.units.fw_mux_selb = new MuxFw(this.wires.p_ex.selb,
            this.wires.p_ex.read_data2, this.trash.trash_wire32,
            this.wires.p_mem.alu_result, this.wires.p_wb.write_data,
            this.wires.p_ex.read_register1, this.wires.p_ex.read_register2);
        this.units.mux_alusrc = new Mux(this.wires.p_ex.alusrc,
            [this.wires.p_ex.read_data2,
            this.wires.p_ex.extended_immediate],
            this.wires.p_ex.alu_input2);

        this.units.data_mem = new DataMem(this.wires.p_mem.memread,
            this.wires.p_mem.memwrite, this.wires.p_mem.alu_result,
            this.wires.p_mem.read_data2, this.wires.p_mem.read_data_mem,
            this.wires.p_mem.instruction);

        this.hazard_mode = 'none';
        this.reset_info();
    }


    /**
     * Turns off pipelining and switches to sequential execution mode
     */
    turn_off_pipelining() {
        this.pipelining_mode = false;
        this.set_to_no_detection();
    }

    /**
     * Turns on pipelining.
     */
    turn_on_pipelining() {
        this.pipelining_mode = true;
    }

    /**
     * Adds support for shift operations.
     */
    add_support_for_shift() {
        this.support_for_shift = true;
        // to update the correct units
        switch (this.hazard_mode) {
            case 'forwarding':
                this.set_to_forwarding();
                break;
            case 'conflict':
                this.set_to_conflict_detection();
                break;
            case 'none':
                this.set_to_no_detection();
                break;
        }
    }

    /**
     * Removes the support for shift operations.
     */
    remove_support_for_shift() {
        this.support_for_shift = false;
        // to update the correct units
        switch (this.hazard_mode) {
            case 'forwarding':
                this.set_to_forwarding();
                break;
            case 'conflict':
                this.set_to_conflict_detection();
                break;
            case 'none':
                this.set_to_no_detection();
                break;
        }
    }

    /**
     * Constructs the instruction out of operation and operands.
     * @param {string} operation - The operation e.g. "ADD"
     * @param {string} operands - The operands as an array ordered the same way as the format.
     */
    make_instruction(operation, operands) {
        let format = Types[operation].format;
        let instr = null;
        switch (format) {
            case 'N':
                instr = Types[operation].make();
                break;

            case 'J':
                instr = Types[operation].make(operands[0]);
                break;

            case 'R':
                if (operation == 'SLL' || operation == 'SRL') {
                    instr = Types[operation].make(operands[2], operands[1], operands[0]);
                } else {
                    instr = Types[operation].make(operands[1], operands[2], operands[0]);
                }
                break;

            case 'I':
                if (operation == "LW" || operation == "SW") {
                    instr = Types[operation].make(operands[2], operands[0], operands[1]);
                } else {
                    instr = Types[operation].make(operands[1], operands[0], operands[2]);
                }
                break;
        }
        return instr;
    }
}
